'use client';

import { useEffect, useRef, useState } from 'react';
import CarouselControls from './CarouselControls';
import CarouselIntro from './CarouselIntro';
import ConcernCard from './ConcernCard';
import { useHorizontalSwipe } from './hooks/useHorizontalSwipe';
import { useIsMobile } from './hooks/useIsMobile';
import {
  WhatIsYourConcern,
  ConcernCard as ConcernCardType,
  Treatment,
} from '@/types/components/whatIsYourConcern';

type Props = {
  data: Pick<WhatIsYourConcern, 'heading' | 'description'>;
  concerns: ConcernCardType[];
  onConcernChange?: (payload: {
    concernHeading?: string;
    treatments: Treatment[];
  }) => void;
  concernOverlayClassName?: string;
};

const ConcernsCarouselClient = ({
  data,
  concerns,
  onConcernChange,
  concernOverlayClassName,
}: Props) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<(HTMLDivElement | null)[]>([]);

  const isMobile = useIsMobile();
  const lastIndex = concerns.length - 1;

  useEffect(() => {
    const activeConcern = concerns[activeIndex];
    const treatments = activeConcern?.treatments ?? [];

    onConcernChange?.({
      concernHeading: activeConcern?.heading?.content,
      treatments,
    });
  }, [activeIndex, concerns, onConcernChange]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const activeElement = itemsRef.current[activeIndex];

    if (!scroller || !activeElement) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const elementRect = activeElement.getBoundingClientRect();

    const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
    const elementCenter = elementRect.left + elementRect.width / 2;
    const delta = elementCenter - scrollerCenter;

    requestAnimationFrame(() => {
      scroller.scrollBy({
        left: delta,
        behavior: 'smooth',
      });
    });
  }, [activeIndex]);

  const handleSelect = (index: number) => {
    setActiveIndex(index);

    const treatmentsSection = document.getElementById('treatments');
    treatmentsSection?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleNext = () => {
    setActiveIndex((prev) => Math.min(prev + 1, lastIndex));
  };

  const handlePrev = () => {
    setActiveIndex((prev) => Math.max(prev - 1, 0));
  };

  const { onTouchStart, onTouchMove, onTouchEnd } = useHorizontalSwipe({
    onSwipeLeft: handleNext,
    onSwipeRight: handlePrev,
  });

  if (!concerns.length) return null;

  return (
    <div>
      <div className="relative">
        <div className="mb-8 overflow-x-hidden">
          <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-4">
            <CarouselIntro data={data} />

            <div className="col-span-1 rounded-[10px] lg:col-span-3">
              <div
                ref={scrollerRef}
                className="hide-scrollbar snap-x snap-mandatory overflow-x-auto scroll-smooth py-3"
                style={{ touchAction: 'pan-y' }}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
              >
                <div className="flex w-max gap-4 pr-6 pl-2 md:pr-20">
                  {concerns.map((item, index) => {
                    const isActive = index === activeIndex;
                    const disableInteraction = isMobile && !isActive;
                    const isHovered = hoveredIndex === index;

                    const shouldScaleDownActive =
                      isActive &&
                      hoveredIndex !== null &&
                      hoveredIndex !== index;

                    return (
                      <ConcernCard
                        key={index}
                        item={item}
                        isActive={isActive}
                        isHovered={isHovered}
                        shouldScaleDownActive={shouldScaleDownActive}
                        disableInteraction={disableInteraction}
                        onSelect={() => handleSelect(index)}
                        onHoverChange={(value) =>
                          setHoveredIndex(value ? index : null)
                        }
                        itemRef={(element) => {
                          itemsRef.current[index] = element;
                        }}
                        overlayClassName={concernOverlayClassName}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CarouselControls
        onPrev={handlePrev}
        onNext={handleNext}
        disablePrev={activeIndex === 0}
        disableNext={activeIndex === lastIndex}
      />
    </div>
  );
};

export default ConcernsCarouselClient;
